import type { ColumnType } from "antd/lib/table";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { CompetitorTypes } from "~enums/competitorTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { sortStringValues } from "~utils/utilities";
import {
  ActionsColumn,
  EntityColumn,
  FixtureNameColumn,
  FixtureNumberColumn,
  PersonsColumn,
  PoolColumn,
  RoundNumberColumn,
  ScoreColumn,
  SeriesColumn,
  StageColumn,
  StartDateColumn,
  StartTimeColumn,
  StatusColumn,
  TypeColumn,
  VenueColumn,
} from "../components";
import type { FixtureTransformed } from "../types";
import { isFixtureEditable } from "../utilities";

export const useFixtureTableColumns = (
  massEditMode: boolean,
  editFixtureId: string,
  onInlineEditClicked: (fixtureId: string) => void,
  onModalEditClicked: (fixture: any) => void,
  onLockUnlockClicked: (fixtureId: string) => void,
): ColumnType<any>[] => {
  const { formatMessage } = useIntl();
  const sportConfiguration = useSportConfiguration();

  const { hasPermission } = useUserPermissions();
  const hasPermissionToEditFixture = hasPermission(PermissionNames.FIXTURE, PermissionOperations.UPDATE);
  const hasPermissionToDeleteFixture = hasPermission(PermissionNames.FIXTURE, PermissionOperations.DELETE);
  const hasPermissionToUpdateFixtureSettings = hasPermission(PermissionNames.FIXTURE_SETTINGS, PermissionOperations.UPDATE);

  const isEditModeEnabled = (fixture: FixtureTransformed) => `${editFixtureId}` === `${fixture.fixtureId}` || massEditMode;

  const homeCompetitor = {
    align: "end",
    title: formatMessage({ id: "fixtureListPage.table.column.homeCompetitor" }),
    key: "homeCompetitor",
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(a.homeCompetitor?.entity?.nameFullLocal, b.homeCompetitor?.entity?.nameFullLocal),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <EntityColumn
          editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
          fixtureId={fixture.fixtureId}
          entity={fixture.homeCompetitor?.entity}
          isHome
          oppositeEntity={fixture.awayCompetitor?.entity}
        />
      );
    },
  };

  const homeParticipants = {
    align: "end",
    title: formatMessage({
      id: "fixtureListPage.table.column.homeParticipants",
    }),
    key: "homeParticipants",
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(
        a.homeParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
        b.homeParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
      ),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <PersonsColumn
          editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
          fixtureId={fixture.fixtureId}
          persons={fixture.homeParticipants?.map((x: any) => x.person)}
          isHome
          oppositePersons={fixture.awayParticipants?.map((x: any) => x.person)}
        />
      );
    },
  };

  const awayCompetitor = {
    title: formatMessage({ id: "fixtureListPage.table.column.awayCompetitor" }),
    key: "awayCompetitor",
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(a.awayCompetitor?.entity?.nameFullLocal, b.awayCompetitor?.entity?.nameFullLocal),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <EntityColumn
          editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
          fixtureId={fixture.fixtureId}
          entity={fixture.awayCompetitor?.entity}
          isHome={false}
          oppositeEntity={fixture.homeCompetitor?.entity}
        />
      );
    },
  };

  const awayParticipants = {
    title: formatMessage({
      id: "fixtureListPage.table.column.awayParticipants",
    }),
    key: "awayParticipants",
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(
        a.awayParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
        b.awayParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
      ),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <PersonsColumn
          editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
          fixtureId={fixture.fixtureId}
          persons={fixture.awayParticipants?.map((x: any) => x.person)}
          isHome={false}
          oppositePersons={fixture.homeParticipants?.map((x: any) => x.person)}
        />
      );
    },
  };

  let away = {};
  let home = {};

  if (sportConfiguration.competitorType === CompetitorTypes.Entity) {
    away = awayCompetitor;
    home = homeCompetitor;
  } else if (sportConfiguration.competitorType === CompetitorTypes.Person) {
    away = awayParticipants;
    home = homeParticipants;
  } else {
    throw new Error(`Unsupported competitor type: ${sportConfiguration.competitorType}`);
  }

  const columns = [
    {
      title: formatMessage({ id: "fixtureListPage.table.column.fixtureId" }),
      dataIndex: "fixtureId",
      key: "fixtureId",
      width: 200,
      align: "center",
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.externalId" }),
      dataIndex: "externalId",
      key: "externalId",
      align: "center",
      width: 150,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.roundNumber" }),
      dataIndex: "roundNumber",
      key: "roundNumber",
      width: 80,
      align: "center",
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.roundNumber ?? "", b.roundNumber ?? ""),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <RoundNumberColumn
            editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    home,
    {
      title: formatMessage({ id: "fixtureListPage.table.column.result" }),
      key: "result",
      align: "center",
      width: 110,
      render: (_, fixture: FixtureTransformed) => {
        return (
          <ScoreColumn
            editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={fixture}
          />
        );
      },
    },
    away,
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.fixtureNumber",
      }),
      dataIndex: "fixtureNumber",
      key: "fixtureNumber",
      align: "center",
      width: 80,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => (a.fixtureNumber ?? 0) - (b.fixtureNumber ?? 0),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <FixtureNumberColumn
            editMode={isFixtureEditable(fixture, false) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.name" }),
      dataIndex: "nameLocal",
      key: "nameLocal",
      align: "center",
      width: 100,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.nameLocal ?? "", b.nameLocal ?? ""),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <FixtureNameColumn
            editMode={isFixtureEditable(fixture, false) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.nameLatin" }),
      dataIndex: "nameLatin",
      key: "nameLatin",
      align: "center",
      width: 100,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.nameLatin ?? "", b.nameLatin ?? ""),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <FixtureNameColumn
            editMode={isFixtureEditable(fixture, false) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.venue" }),
      dataIndex: "venueId",
      key: "venueId",
      width: 135,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.venue?.nameLocal, b.venue?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return (
          <VenueColumn
            editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            venue={fixture.venue}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.date" }),
      dataIndex: "startDate",
      key: "startDate",
      align: "center",
      width: 110,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime(),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <StartDateColumn
            editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.startTime" }),
      dataIndex: "startTime",
      key: "startTime",
      align: "center",
      width: 80,
      sorter: (a, b) => sortStringValues(a.startTime?.split("T")?.[1], b.startTime?.split("T")?.[1]),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <StartTimeColumn
            editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.type" }),
      dataIndex: "fixtureType",
      key: "fixtureType",
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.fixtureType, b.fixtureType),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <TypeColumn
            editMode={isFixtureEditable(fixture, false) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.status" }),
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.status, b.status),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <StatusColumn
            editMode={isFixtureEditable(fixture, false) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            value={value}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.stage" }),
      dataIndex: "stageCode",
      key: "stage",
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.stage?.nameLocal, b.stage?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return (
          <StageColumn
            editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            stage={fixture.stage}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.stageCode" }),
      dataIndex: "stageCode",
      key: "stageCode",
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.pool" }),
      dataIndex: "poolCode",
      key: "pool",
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.pool?.nameLocal, b.pool?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return (
          <PoolColumn
            editMode={isFixtureEditable(fixture, true) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            pool={fixture.pool}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.poolCode" }),
      dataIndex: "poolCode",
      key: "poolCode",
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.series" }),
      dataIndex: "seriesCode",
      key: "series",
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.series?.nameLocal, b.series?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return (
          <SeriesColumn
            editMode={isFixtureEditable(fixture, false) && isEditModeEnabled(fixture)}
            fixtureId={fixture.fixtureId}
            fixture={fixture}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.seriesCode" }),
      dataIndex: "seriesCode",
      key: "seriesCode",
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.added" }),
      dataIndex: "added",
      key: "added",
      align: "center",
      width: 120,
      render: (value: string) => dayjs(value).format("DD/MM/YYYY\tHH:mm"),
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.attendance" }),
      dataIndex: "attendance",
      key: "attendance",
      align: "center",
      width: 70,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.discipline" }),
      dataIndex: "discipline",
      key: "discipline",
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.startTimeActualUtc",
      }),
      dataIndex: "startTimeActualUtc",
      key: "startTimeActualUtc",
      align: "center",
      width: 140,
      render: (value: string) => value && dayjs(value).format("DD/MM/YYYY\tHH:mm"),
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.endTimeActualUtc",
      }),
      dataIndex: "endTimeActualUtc",
      key: "endTimeActualUtc",
      align: "center",
      width: 120,
      render: (value: string) => value && dayjs(value).format("DD/MM/YYYY\tHH:mm"),
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.fixtureProfile",
      }),
      dataIndex: "fixtureProfile",
      key: "fixtureProfile",
      align: "center",
      width: 100,
      render: (_, fixture: FixtureTransformed) => fixture?.fixtureProfile?.name,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.profileId" }),
      dataIndex: "profileId",
      key: "profileId",
      align: "center",
      width: 200,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.internationalReference",
      }),
      dataIndex: "internationalReference",
      key: "internationalReference",
      align: "center",
      width: 140,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.round" }),
      dataIndex: "round",
      key: "round",
      width: 120,
      align: "center",
      render: (_, fixture: FixtureTransformed) => fixture.round?.nameLocal,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.roundCode" }),
      dataIndex: "roundCode",
      key: "roundCode",
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.seriesFixtureNumber",
      }),
      dataIndex: "seriesFixtureNumber",
      key: "seriesFixtureNumber",
      align: "center",
      width: 140,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.timesUnconfirmed",
      }),
      dataIndex: "timesUnconfirmed",
      key: "timesUnconfirmed",
      align: "center",
      width: 140,
      render: (value: string) => value,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.startTimeUtc" }),
      dataIndex: "startTimeUtc",
      key: "startTimeUtc",
      align: "center",
      width: 140,
      render: (value: string) => value && dayjs(value).format("DD/MM/YYYY\tHH:mm"),
    },
  ];

  if (hasPermissionToEditFixture || hasPermissionToDeleteFixture || hasPermissionToUpdateFixtureSettings) {
    columns.push({
      align: "right",
      fixed: "right",
      width: 130,
      render: (_: string, fixture: FixtureTransformed) => {
        return (
          <ActionsColumn
            editMode={false}
            fixtureId={fixture.fixtureId}
            value={fixture}
            editFixtureId={editFixtureId}
            massEditMode={massEditMode}
            onInlineEditClicked={onInlineEditClicked}
            onModalEditClicked={onModalEditClicked}
            onLockUnlockClicked={onLockUnlockClicked}
          />
        );
      },
    });
  }

  return columns;
};
